import { authApi } from '@/api/auth'
import { tokenStore } from '@/api/http'
import { queryClient } from '@/api/queryClient'
import { useAccountStore } from '@/stores/account'
import { useAuthStore } from '@/stores/auth'
import { useKycStore } from '@/stores/kyc'

/**
 * 会话清理 —— 换客户时必须把上一位留下的一切从内存里抹掉。
 *
 * ## 为什么要有这个文件
 *
 * 门户是单页应用：退出登录与登录成功都走 `router.replace`，**不刷新页面**。
 * 所以 Pinia 的 store 与 TanStack Query 的缓存会原样活到下一个会话里。
 * 原先各处只清了 token 和 auth store，账户 store 与查询缓存没人管，结果是
 * **上一位客户的 MT5 登录号会显示给下一位客户**（平台下载页、概览、我的账户、
 * 入金、出金、报表——凡是读账户 store 的页面都会）。
 *
 * 而且它不会自愈：账户列表那个查询的 `enabled` 挂在 `accounts` 能力上，
 * 这个能力只有 `MT5_OPENED` 才有。新客户还没开通 MT5 → 请求根本不发 →
 * 旧数据永远不被覆盖，直到整页重载（Ctrl+F5）才消失。
 *
 * ## 为什么每个入口都要调
 *
 * 会话结束有四条路，**只在「点退出」那条清是不够的**：
 *   ① 主动退出（`logoutSession`）
 *   ② 401 / 会话过期被踢（`api/hooks.ts`）
 *   ③ 刷新后 `/profile/me` 拉不到、守卫打回登录页（`router/index.ts`）
 *   ④ 登录准入被拒，残留 token 要清（`views/auth/LoginView.vue`）
 * 登录**成功**时也再清一次（同上 LoginView）：这是兜底，
 * 任何一条路将来漏了，新会话开始的那一刻也会被纠正。
 */
export function clearSession() {
  /**
   * 顺序有讲究：**先重置 auth，再清查询缓存**。
   * auth 一重置，各页面查询的 `enabled`（都挂在 `auth.can(...)` 上）立刻变 false，
   * 此后清缓存不会触发任何重新拉取——否则 token 刚清掉就发请求，必然 401，
   * 又会绕回 `onUnauthorized`。
   */
  useAuthStore().reset()
  useAccountStore().reset()
  useKycStore().reset()
  tokenStore.clear()
  queryClient.clear()
}

/**
 * 主动退出登录：先通知服务端吊销会话，再清本地。
 *
 * 服务端那一步失败也照清不误——会话可能本来就过期了，
 * 把客户留在一个「点了退出却还显示着自己资料」的页面上更糟。
 */
export async function logoutSession() {
  try {
    await authApi.logout()
  } catch {
    // 服务端会话可能已过期，本地照样清干净
  }
  clearSession()
}
